import React from 'react';
import { useIntl } from 'react-intl';
import { Alert, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { canStartPublish } from 'fym-shared/src/music24/oneOffPublish';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useGetAssetPublishDefaultsQuery } from '../../../../redux/api/fymAPI';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { closePublishDialog, PublishDialogFile, PublishDialogRequest } from './publishDialogStore';
import { publishMessages } from './publishMessages';
import { ONE_OFF_CHANNEL_NAMES, publishRefusalText } from './publishBadgeText';
import { PublishForm } from './PublishForm';
import { PublishStatusPanel } from './PublishStatusPanel';
import { PublishManualPanel } from './PublishManualPanel';
import { PublishManualFilesPanel } from './PublishManualFilesPanel';

/**
 * The body of the open dialog: it reads the pre-filled defaults (the channel, the title, the
 * description, the release day and the newest record), then shows the form, the state of a live
 * record, or the manual mark. No Claude Design frame exists yet; it is built from the existing
 * sheets (`ReelApproveSheet`) and tokens.
 */
const PublishDialogBody: React.FC<{ request: PublishDialogRequest }> = ({ request }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const { accountId, kitAssetUuid, channel } = request;
  const { data, error, isLoading, refetch } = useGetAssetPublishDefaultsQuery({ accountId, kitAssetUuid, channel });
  const name = ONE_OFF_CHANNEL_NAMES[channel];

  const header = (
    <ModalHeader toggle={closePublishDialog}>
      {intl.formatMessage(publishMessages.promptTitle, { channel: name })}
      {request.justReady === true && (
        <small className="d-block text-muted fw-normal mt-1">
          {intl.formatMessage(publishMessages.promptReady, { asset: request.assetName })}
        </small>
      )}
    </ModalHeader>
  );

  if (isLoading) {
    return (
      <>
        {header}
        <LoadingView />
      </>
    );
  }
  if (error !== undefined || data === undefined) {
    return (
      <>
        {header}
        <ErrorView error={error} retry={() => refetch().catch(console.error)} />
      </>
    );
  }
  // A manual channel, an Instagram mark, or a file that Instagram does not take as a Reel.
  const isMarkedByHand = data.current?.mode === 'manual' && data.current.status === 'published';
  if (data.mode === 'manual' || isMarkedByHand || (data.refusal !== null && canStartPublish(data.current))) {
    return (
      <>
        {header}
        <PublishManualPanel
          accountId={accountId}
          kitAssetUuid={kitAssetUuid}
          channel={channel}
          record={data.current}
          downloadUrl={request.downloadUrl ?? null}
          fallbackReason={data.refusal === null ? null : publishRefusalText(intl, data.refusal)}
          onClose={closePublishDialog}
        />
      </>
    );
  }
  if (!canStartPublish(data.current) && data.current !== null) {
    return (
      <>
        {header}
        <PublishStatusPanel
          accountId={accountId}
          record={data.current}
          releaseDate={data.releaseDate}
          onClose={closePublishDialog}
        />
      </>
    );
  }
  if (data.connection !== null && !data.connection.connected) {
    return (
      <>
        {header}
        <ModalBody>
          <Alert color="info" className="mb-0">
            {intl.formatMessage(publishMessages.errorNotConnected, { channel: name })}
          </Alert>
        </ModalBody>
        <ModalFooter>
          <SMLButton
            color="primary"
            omitCap
            onClick={() => {
              closePublishDialog();
              router.push(SMLRoutes.promoteChannels);
            }}
          >
            {intl.formatMessage(publishMessages.actionConnect, { channel: name })}
          </SMLButton>
        </ModalFooter>
      </>
    );
  }
  return (
    <>
      {header}
      <PublishForm accountId={accountId} defaults={data} onDone={closePublishDialog} />
    </>
  );
};

interface PublishDialogProps {
  request: PublishDialogRequest | null;
}

/** The manual mark of a slot with several files: one row per file (owner fix 2026-10-10). */
const PublishDialogFilesBody: React.FC<{ request: PublishDialogRequest; files: PublishDialogFile[] }> = ({
  request,
  files,
}) => {
  const intl = useIntl();
  return (
    <>
      <ModalHeader toggle={closePublishDialog}>
        {intl.formatMessage(publishMessages.promptTitle, { channel: ONE_OFF_CHANNEL_NAMES[request.channel] })}
      </ModalHeader>
      <PublishManualFilesPanel
        accountId={request.accountId}
        catalogItemUuid={request.catalogItemUuid}
        channel={request.channel}
        files={files}
        onClose={closePublishDialog}
      />
    </>
  );
};

/** "Publish to YouTube?" (owner decision 2026-10-09). The body mounts on open, so each open starts fresh. */
const PublishDialogComponent: React.FC<PublishDialogProps> = ({ request }) => {
  const files = request?.files;
  return (
    <Modal isOpen={request !== null} toggle={closePublishDialog} centered data-testid="publish-dialog">
      {request !== null &&
        (files !== undefined && files.length > 1 ? (
          <PublishDialogFilesBody
            key={`${request.catalogItemUuid}:${request.channel}`}
            request={request}
            files={files}
          />
        ) : (
          <PublishDialogBody key={`${request.kitAssetUuid}:${request.channel}`} request={request} />
        ))}
    </Modal>
  );
};

export const PublishDialog = TypedMemo(PublishDialogComponent);
